/**
 * DocumentArrangementsSection
 *
 * Manages document arrangement cards for a receiver business partner.
 * Each arrangement links to a destination port and contains:
 * - Notify parties
 * - Document recipients
 * - Stakeholders
 * - Special instructions
 */

import { useState, useEffect, useRef } from 'react';
import { Plus, Trash01, XCircle, UploadCloud02, File06, Edit05, Check } from '@untitledui/icons';
import { Input } from '@/components/base/input/input';
import { Button } from '@/components/base/buttons/button';
import { Select } from '@/components/base/select/select';
import { TextArea } from '@/components/base/textarea/textarea';
import { usePorts } from '@/hooks/api/use-ports';
import type {
  NotifyParty,
  DocumentRecipient,
  Stakeholder,
} from '@/types/document-arrangement';

// ============================================================================
// LOCAL ARRANGEMENT TYPE (for form state, before saving)
// ============================================================================

export interface ArrangementFormData {
  arrangement_id?: string; // present if editing existing
  destination_port_id: string;
  destination_port_name: string;
  destination_country_code: string;
  notify_parties: NotifyParty[];
  document_recipients: DocumentRecipient[];
  stakeholders: Stakeholder[];
  special_instructions: string;
  file?: File; // attached PDF to upload on save
}

interface DocumentArrangementsSectionProps {
  receiverName: string;
  receiverCompanyId?: string;
  arrangements: ArrangementFormData[];
  onArrangementsChange: (arrangements: ArrangementFormData[]) => void;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function DocumentArrangementsSection({
  receiverCompanyId,
  arrangements,
  onArrangementsChange,
}: DocumentArrangementsSectionProps) {
  const handleAddArrangement = () => {
    onArrangementsChange([
      ...arrangements,
      {
        destination_port_id: '',
        destination_port_name: '',
        destination_country_code: '',
        notify_parties: [],
        document_recipients: [],
        stakeholders: [],
        special_instructions: '',
      },
    ]);
  };

  const handleRemoveArrangement = (index: number) => {
    onArrangementsChange(arrangements.filter((_, i) => i !== index));
  };

  const handleUpdateArrangement = (index: number, updated: ArrangementFormData) => {
    const next = [...arrangements];
    next[index] = updated;
    onArrangementsChange(next);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-primary">Document Arrangements</h2>
        <Button
          type="button"
          color="secondary"
          size="sm"
          iconLeading={Plus}
          onClick={handleAddArrangement}
        >
          Add Port Arrangement
        </Button>
      </div>

      {arrangements.length === 0 && (
        <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary text-center">
          <p className="text-sm text-tertiary">
            No document arrangements yet. Add a port arrangement to define notify parties,
            document recipients, and stakeholders for this receiver.
          </p>
        </div>
      )}

      {arrangements.map((arrangement, index) => (
        <DocumentArrangementCard
          key={index}
          arrangement={arrangement}
          receiverCompanyId={receiverCompanyId}
          onUpdate={(updated) => handleUpdateArrangement(index, updated)}
          onRemove={() => handleRemoveArrangement(index)}
        />
      ))}
    </div>
  );
}

// ============================================================================
// ARRANGEMENT CARD (exported for reuse, e.g. shipper operational routes)
// ============================================================================

export type DocumentArrangementCardProps = {
  arrangement: ArrangementFormData;
  receiverCompanyId?: string;
  onUpdate: (updated: ArrangementFormData) => void;
  onRemove: () => void;
  /** Destination port is fixed (e.g. matches operational trade route). */
  readOnlyDestination?: boolean;
  /** Hide per-card remove control when parent owns add/remove (e.g. route row). */
  hideRemoveButton?: boolean;
};

export function DocumentArrangementCard({
  arrangement,
  onUpdate,
  onRemove,
  readOnlyDestination = false,
  hideRemoveButton = false,
}: DocumentArrangementCardProps) {
  // File upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleFileSelect = (file: File) => {
    if (!file.type.includes('pdf')) return;
    onUpdate({ ...arrangement, file });
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleClearFile = () => {
    onUpdate({ ...arrangement, file: undefined });
  };

  // Port search
  const [portSearch, setPortSearch] = useState('');
  const [debouncedPortSearch, setDebouncedPortSearch] = useState('');
  const [showPortDropdown, setShowPortDropdown] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedPortSearch(portSearch), 500);
    return () => clearTimeout(timer);
  }, [portSearch]);

  const { data: portsData } = usePorts(debouncedPortSearch, undefined, debouncedPortSearch.length >= 2);
  const portOptions = portsData?.ports || [];

  // Inline add states
  const [showAddNotifyParty, setShowAddNotifyParty] = useState(false);
  const [showAddRecipient, setShowAddRecipient] = useState(false);
  const [showAddStakeholder, setShowAddStakeholder] = useState(false);

  const [newNotifyParty, setNewNotifyParty] = useState<NotifyParty>({});
  const [newRecipient, setNewRecipient] = useState<DocumentRecipient>({});
  const [newStakeholder, setNewStakeholder] = useState<Stakeholder>({});

  // Inline edit states
  const [editingNotifyIdx, setEditingNotifyIdx] = useState<number | null>(null);
  const [editNotifyParty, setEditNotifyParty] = useState<NotifyParty>({});
  const [editingRecipientIdx, setEditingRecipientIdx] = useState<number | null>(null);
  const [editRecipient, setEditRecipient] = useState<DocumentRecipient>({});
  const [editingStakeholderIdx, setEditingStakeholderIdx] = useState<number | null>(null);
  const [editStakeholder, setEditStakeholder] = useState<Stakeholder>({});

  const handlePortSelect = (portId: string) => {
    const port = portOptions.find((p) => p.port_id === portId);
    if (port) {
      onUpdate({
        ...arrangement,
        destination_port_id: port.port_id,
        destination_port_name: port.display_name,
        destination_country_code: port.country_code,
      });
    }
    setPortSearch('');
    setShowPortDropdown(false);
  };

  const handleClearPort = () => {
    onUpdate({
      ...arrangement,
      destination_port_id: '',
      destination_port_name: '',
      destination_country_code: '',
    });
  };

  // Notify party handlers
  const handleAddNotifyParty = () => {
    if (!newNotifyParty.name?.trim()) return;
    onUpdate({
      ...arrangement,
      notify_parties: [...arrangement.notify_parties, { ...newNotifyParty }],
    });
    setNewNotifyParty({});
    setShowAddNotifyParty(false);
  };

  const handleRemoveNotifyParty = (idx: number) => {
    onUpdate({
      ...arrangement,
      notify_parties: arrangement.notify_parties.filter((_, i) => i !== idx),
    });
    if (editingNotifyIdx === idx) setEditingNotifyIdx(null);
  };

  const handleSaveNotifyParty = () => {
    if (editingNotifyIdx === null) return;
    const updated = [...arrangement.notify_parties];
    updated[editingNotifyIdx] = { ...editNotifyParty };
    onUpdate({ ...arrangement, notify_parties: updated });
    setEditingNotifyIdx(null);
  };

  // Document recipient handlers
  const handleAddRecipient = () => {
    if (!newRecipient.name?.trim() && !newRecipient.email?.trim()) return;
    onUpdate({
      ...arrangement,
      document_recipients: [...arrangement.document_recipients, { ...newRecipient }],
    });
    setNewRecipient({});
    setShowAddRecipient(false);
  };

  const handleRemoveRecipient = (idx: number) => {
    onUpdate({
      ...arrangement,
      document_recipients: arrangement.document_recipients.filter((_, i) => i !== idx),
    });
    if (editingRecipientIdx === idx) setEditingRecipientIdx(null);
  };

  const handleSaveRecipient = () => {
    if (editingRecipientIdx === null) return;
    const updated = [...arrangement.document_recipients];
    updated[editingRecipientIdx] = { ...editRecipient };
    onUpdate({ ...arrangement, document_recipients: updated });
    setEditingRecipientIdx(null);
  };

  // Stakeholder handlers
  const handleAddStakeholder = () => {
    if (!newStakeholder.name?.trim()) return;
    onUpdate({
      ...arrangement,
      stakeholders: [...arrangement.stakeholders, { ...newStakeholder }],
    });
    setNewStakeholder({});
    setShowAddStakeholder(false);
  };

  const handleRemoveStakeholder = (idx: number) => {
    onUpdate({
      ...arrangement,
      stakeholders: arrangement.stakeholders.filter((_, i) => i !== idx),
    });
    if (editingStakeholderIdx === idx) setEditingStakeholderIdx(null);
  };

  const handleSaveStakeholder = () => {
    if (editingStakeholderIdx === null) return;
    const updated = [...arrangement.stakeholders];
    updated[editingStakeholderIdx] = { ...editStakeholder };
    onUpdate({ ...arrangement, stakeholders: updated });
    setEditingStakeholderIdx(null);
  };

  const stakeholderTypeOptions = [
    { id: '', label: 'Select type...' },
    { id: 'agent', label: 'Agent' },
    { id: 'broker', label: 'Broker' },
    { id: 'customs', label: 'Customs' },
    { id: 'bank', label: 'Bank' },
    { id: 'insurance', label: 'Insurance' },
    { id: 'other', label: 'Other' },
  ];

  return (
    <div className="rounded-xl bg-primary p-6 shadow-xs ring-1 ring-secondary space-y-6">
      {/* Port + Remove */}
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <label className="block text-sm font-medium text-primary mb-2">
            Destination Port
          </label>
          {readOnlyDestination ? (
            <div className="px-3 py-2 bg-secondary rounded-lg text-sm text-primary">
              {arrangement.destination_port_name
                ? `${arrangement.destination_port_name}${arrangement.destination_country_code ? ` (${arrangement.destination_country_code})` : ''}`
                : '—'}
            </div>
          ) : arrangement.destination_port_name ? (
            <div className="flex items-center gap-2">
              <span className="flex-1 px-3 py-2 bg-secondary rounded-lg text-sm text-primary">
                {arrangement.destination_port_name}
              </span>
              <button
                type="button"
                onClick={handleClearPort}
                className="text-tertiary hover:text-primary"
              >
                <XCircle className="size-5" />
              </button>
            </div>
          ) : (
            <div className="relative">
              <Input
                placeholder="Search port..."
                value={portSearch}
                onChange={(value) => {
                  setPortSearch(value);
                  setShowPortDropdown(true);
                }}
                onFocus={() => setShowPortDropdown(true)}
                onBlur={() => setTimeout(() => setShowPortDropdown(false), 200)}
                size="md"
              />
              {showPortDropdown && portOptions.length > 0 && portSearch.length >= 2 && (
                <div className="absolute z-10 w-full mt-1 max-h-48 overflow-auto rounded-lg border border-secondary bg-primary shadow-lg">
                  {portOptions.map((port) => (
                    <button
                      key={port.port_id}
                      type="button"
                      className="w-full px-3 py-2 text-left text-sm text-primary hover:bg-secondary"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handlePortSelect(port.port_id)}
                    >
                      {port.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {!hideRemoveButton && (
          <button
            type="button"
            onClick={onRemove}
            className="mt-7 text-tertiary hover:text-error-500"
            title="Remove arrangement"
          >
            <Trash01 className="size-5" />
          </button>
        )}
      </div>

      {/* PDF Upload Dropzone */}
      {arrangement.file ? (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-brand-50 border border-brand-200">
          <File06 className="size-5 text-brand-600 shrink-0" />
          <span className="flex-1 text-sm text-brand-700 font-medium truncate">
            {arrangement.file.name}
          </span>
          <button
            type="button"
            onClick={handleClearFile}
            className="text-brand-400 hover:text-brand-600"
          >
            <XCircle className="size-4" />
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
          onDragLeave={() => setIsDragOver(false)}
          onDrop={handleDrop}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
          className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${
            isDragOver
              ? 'border-brand-400 bg-brand-50'
              : 'border-secondary hover:border-brand-300 hover:bg-secondary'
          }`}
        >
          <UploadCloud02 className="size-5 text-tertiary" />
          <span className="text-sm text-tertiary">Upload Arrangement PDF</span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf"
            className="hidden"
            onChange={handleFileInputChange}
          />
        </div>
      )}

      {/* Notify Parties */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-primary">Notify Parties</h3>
          <Button
            type="button"
            color="secondary"
            size="sm"
            iconLeading={Plus}
            onClick={() => setShowAddNotifyParty(true)}
          >
            Add
          </Button>
        </div>
        {arrangement.notify_parties.length > 0 && (
          <div className="space-y-2 mb-3">
            {arrangement.notify_parties.map((party, idx) =>
              editingNotifyIdx === idx ? (
                <div key={idx} className="grid grid-cols-1 gap-3 sm:grid-cols-5 items-end p-2 bg-secondary rounded-lg ring-1 ring-brand-300">
                  <Input placeholder="Name" value={editNotifyParty.name || ''} onChange={(v) => setEditNotifyParty({ ...editNotifyParty, name: v })} size="sm" />
                  <Input placeholder="Company" value={editNotifyParty.company || ''} onChange={(v) => setEditNotifyParty({ ...editNotifyParty, company: v })} size="sm" />
                  <Input placeholder="Email" value={editNotifyParty.email || ''} onChange={(v) => setEditNotifyParty({ ...editNotifyParty, email: v })} size="sm" />
                  <Input placeholder="Phone" value={editNotifyParty.phone || ''} onChange={(v) => setEditNotifyParty({ ...editNotifyParty, phone: v })} size="sm" />
                  <div className="flex gap-2">
                    <Button type="button" color="primary" size="sm" iconLeading={Check} onClick={handleSaveNotifyParty}>Save</Button>
                    <Button type="button" color="secondary" size="sm" onClick={() => setEditingNotifyIdx(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div
                  key={idx}
                  className="flex items-center gap-3 px-3 py-2 bg-secondary rounded-lg text-sm group"
                >
                  <span className="flex-1 text-primary">{party.name || '-'}</span>
                  <span className="text-tertiary">{party.company || ''}</span>
                  <span className="text-tertiary">{party.email || ''}</span>
                  <span className="text-tertiary">{party.phone || ''}</span>
                  <button
                    type="button"
                    onClick={() => { setEditingNotifyIdx(idx); setEditNotifyParty({ ...party }); }}
                    className="text-tertiary hover:text-brand-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Edit"
                  >
                    <Edit05 className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveNotifyParty(idx)}
                    className="text-tertiary hover:text-error-500"
                  >
                    <XCircle className="size-4" />
                  </button>
                </div>
              )
            )}
          </div>
        )}
        {showAddNotifyParty && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-5 items-end">
            <Input
              placeholder="Name"
              value={newNotifyParty.name || ''}
              onChange={(v) => setNewNotifyParty({ ...newNotifyParty, name: v })}
              size="sm"
            />
            <Input
              placeholder="Company"
              value={newNotifyParty.company || ''}
              onChange={(v) => setNewNotifyParty({ ...newNotifyParty, company: v })}
              size="sm"
            />
            <Input
              placeholder="Email"
              value={newNotifyParty.email || ''}
              onChange={(v) => setNewNotifyParty({ ...newNotifyParty, email: v })}
              size="sm"
            />
            <Input
              placeholder="Phone"
              value={newNotifyParty.phone || ''}
              onChange={(v) => setNewNotifyParty({ ...newNotifyParty, phone: v })}
              size="sm"
            />
            <div className="flex gap-2">
              <Button type="button" color="primary" size="sm" onClick={handleAddNotifyParty}>
                Add
              </Button>
              <Button
                type="button"
                color="secondary"
                size="sm"
                onClick={() => {
                  setShowAddNotifyParty(false);
                  setNewNotifyParty({});
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Document Recipients */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-primary">Document Recipients</h3>
          <Button
            type="button"
            color="secondary"
            size="sm"
            iconLeading={Plus}
            onClick={() => setShowAddRecipient(true)}
          >
            Add
          </Button>
        </div>
        {arrangement.document_recipients.length > 0 && (
          <div className="space-y-2 mb-3">
            {arrangement.document_recipients.map((recipient, idx) =>
              editingRecipientIdx === idx ? (
                <div key={idx} className="grid grid-cols-1 gap-3 sm:grid-cols-4 items-end p-2 bg-secondary rounded-lg ring-1 ring-brand-300">
                  <Input placeholder="Name" value={editRecipient.name || ''} onChange={(v) => setEditRecipient({ ...editRecipient, name: v })} size="sm" />
                  <Input placeholder="Email" value={editRecipient.email || ''} onChange={(v) => setEditRecipient({ ...editRecipient, email: v })} size="sm" />
                  <Input placeholder="Purpose" value={editRecipient.purpose || ''} onChange={(v) => setEditRecipient({ ...editRecipient, purpose: v })} size="sm" />
                  <div className="flex gap-2">
                    <Button type="button" color="primary" size="sm" iconLeading={Check} onClick={handleSaveRecipient}>Save</Button>
                    <Button type="button" color="secondary" size="sm" onClick={() => setEditingRecipientIdx(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div
                  key={idx}
                  className="flex items-center gap-3 px-3 py-2 bg-secondary rounded-lg text-sm group"
                >
                  <span className="flex-1 text-primary">{recipient.name || '-'}</span>
                  <span className="text-tertiary">{recipient.email || ''}</span>
                  <span className="text-tertiary">{recipient.purpose || ''}</span>
                  <button
                    type="button"
                    onClick={() => { setEditingRecipientIdx(idx); setEditRecipient({ ...recipient }); }}
                    className="text-tertiary hover:text-brand-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Edit"
                  >
                    <Edit05 className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveRecipient(idx)}
                    className="text-tertiary hover:text-error-500"
                  >
                    <XCircle className="size-4" />
                  </button>
                </div>
              )
            )}
          </div>
        )}
        {showAddRecipient && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4 items-end">
            <Input
              placeholder="Name"
              value={newRecipient.name || ''}
              onChange={(v) => setNewRecipient({ ...newRecipient, name: v })}
              size="sm"
            />
            <Input
              placeholder="Email"
              value={newRecipient.email || ''}
              onChange={(v) => setNewRecipient({ ...newRecipient, email: v })}
              size="sm"
            />
            <Input
              placeholder="Purpose"
              value={newRecipient.purpose || ''}
              onChange={(v) => setNewRecipient({ ...newRecipient, purpose: v })}
              size="sm"
            />
            <div className="flex gap-2">
              <Button type="button" color="primary" size="sm" onClick={handleAddRecipient}>
                Add
              </Button>
              <Button
                type="button"
                color="secondary"
                size="sm"
                onClick={() => {
                  setShowAddRecipient(false);
                  setNewRecipient({});
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Stakeholders */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-primary">Stakeholders</h3>
          <Button
            type="button"
            color="secondary"
            size="sm"
            iconLeading={Plus}
            onClick={() => setShowAddStakeholder(true)}
          >
            Add
          </Button>
        </div>
        {arrangement.stakeholders.length > 0 && (
          <div className="space-y-2 mb-3">
            {arrangement.stakeholders.map((stakeholder, idx) =>
              editingStakeholderIdx === idx ? (
                <div key={idx} className="grid grid-cols-1 gap-3 sm:grid-cols-5 items-end p-2 bg-secondary rounded-lg ring-1 ring-brand-300">
                  <Input placeholder="Name" value={editStakeholder.name || ''} onChange={(v) => setEditStakeholder({ ...editStakeholder, name: v })} size="sm" />
                  <Select
                    placeholder="Type"
                    items={stakeholderTypeOptions}
                    selectedKey={editStakeholder.type || ''}
                    onSelectionChange={(key) => setEditStakeholder({ ...editStakeholder, type: key as string })}
                    size="sm"
                  >
                    {(item) => (
                      <Select.Item key={item.id} id={item.id}>
                        {item.label}
                      </Select.Item>
                    )}
                  </Select>
                  <Input placeholder="Contact info" value={editStakeholder.contact_info || ''} onChange={(v) => setEditStakeholder({ ...editStakeholder, contact_info: v })} size="sm" />
                  <Input placeholder="Role" value={editStakeholder.role || ''} onChange={(v) => setEditStakeholder({ ...editStakeholder, role: v })} size="sm" />
                  <div className="flex gap-2">
                    <Button type="button" color="primary" size="sm" iconLeading={Check} onClick={handleSaveStakeholder}>Save</Button>
                    <Button type="button" color="secondary" size="sm" onClick={() => setEditingStakeholderIdx(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div
                  key={idx}
                  className="flex items-center gap-3 px-3 py-2 bg-secondary rounded-lg text-sm group"
                >
                  <span className="flex-1 text-primary">{stakeholder.name || '-'}</span>
                  <span className="text-tertiary">{stakeholder.type || ''}</span>
                  <span className="text-tertiary">{stakeholder.contact_info || ''}</span>
                  <span className="text-tertiary">{stakeholder.role || ''}</span>
                  <button
                    type="button"
                    onClick={() => { setEditingStakeholderIdx(idx); setEditStakeholder({ ...stakeholder }); }}
                    className="text-tertiary hover:text-brand-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Edit"
                  >
                    <Edit05 className="size-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemoveStakeholder(idx)}
                    className="text-tertiary hover:text-error-500"
                  >
                    <XCircle className="size-4" />
                  </button>
                </div>
              )
            )}
          </div>
        )}
        {showAddStakeholder && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-5 items-end">
            <Input
              placeholder="Name"
              value={newStakeholder.name || ''}
              onChange={(v) => setNewStakeholder({ ...newStakeholder, name: v })}
              size="sm"
            />
            <Select
              placeholder="Type"
              items={stakeholderTypeOptions}
              selectedKey={newStakeholder.type || ''}
              onSelectionChange={(key) =>
                setNewStakeholder({ ...newStakeholder, type: key as string })
              }
              size="sm"
            >
              {(item) => (
                <Select.Item key={item.id} id={item.id}>
                  {item.label}
                </Select.Item>
              )}
            </Select>
            <Input
              placeholder="Contact info"
              value={newStakeholder.contact_info || ''}
              onChange={(v) => setNewStakeholder({ ...newStakeholder, contact_info: v })}
              size="sm"
            />
            <Input
              placeholder="Role"
              value={newStakeholder.role || ''}
              onChange={(v) => setNewStakeholder({ ...newStakeholder, role: v })}
              size="sm"
            />
            <div className="flex gap-2">
              <Button type="button" color="primary" size="sm" onClick={handleAddStakeholder}>
                Add
              </Button>
              <Button
                type="button"
                color="secondary"
                size="sm"
                onClick={() => {
                  setShowAddStakeholder(false);
                  setNewStakeholder({});
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Special Instructions */}
      <div>
        <label className="block text-sm font-medium text-primary mb-2">
          Special Instructions
        </label>
        <TextArea
          placeholder="Enter any special instructions for document handling..."
          value={arrangement.special_instructions}
          onChange={(v) =>
            onUpdate({ ...arrangement, special_instructions: v })
          }
        />
      </div>
    </div>
  );
}
